<template>
  <fmv-sidebar type="light" variant="white" wheel-propagation>
    <div class="sidebar-heading mt-4" style="font-size: 18px; font-weight: bolder">Reference Guide</div>
    <div class="col-lg-auto mx-2 my-2">
      <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
        <b-form-input class="w-lg-auto" placeholder="Search Sections" v-model="searchQuery" />

        <b-btn variant="flush" type="submit">
          <md-icon>search</md-icon>
        </b-btn>
      </form>
    </div>

    <div>
      <b-tabs pills content-class="p-3" nav-class="px-3 pt-3 bg-light">
        <b-tab active>
          <template #title>
            <i class="material-icons icon-16pt mr-1">functions</i>
            <span class="small">Computed</span>
          </template>
          <div v-for="category in filteredComputedProperties" :key="category.name" class="mb-3">
            <b-badge variant="primary" class="mb-2 text-uppercase" style="font-size: 10px">{{
              category.name
            }}</b-badge>
            <div v-for="prop in category.items" :key="prop.name" class="mb-2">
              <b-card class="border-left-success">
                <div class="d-flex align-items-start justify-content-between mb-2">
                  <code class="text-success font-weight-bold small">{{ prop.name }}</code>
                  <b-button
                    size="sm"
                    variant="link"
                    @click="copyToClipboard(prop.usage)"
                    class="p-0 text-secondary"
                    v-b-tooltip.hover
                    title="Copy to clipboard"
                  >
                    <i class="material-icons icon-16pt">content_copy</i>
                  </b-button>
                </div>
                <p class="text-muted small mb-2">{{ prop.description }}</p>
                <code class="d-block p-2 bg-light rounded small text-wrap">{{ prop.usage }}</code>
              </b-card>
            </div>
          </div>
          <div v-if="filteredComputedProperties.length === 0" class="text-center py-5 text-muted">
            <i class="material-icons" style="font-size: 48px">search_off</i>
            <p class="mb-0">No results found</p>
          </div>
        </b-tab>

        <b-tab>
          <template #title>
            <i class="material-icons icon-16pt mr-1">code</i>
            <span class="small">Methods</span>
          </template>
          <div v-for="category in filteredMethods" :key="category.name" class="mb-3">
            <b-badge variant="warning" class="mb-2 text-uppercase" style="font-size: 10px">{{
              category.name
            }}</b-badge>
            <div v-for="method in category.items" :key="method.name" class="mb-2">
              <b-card class="border-left-warning">
                <div class="d-flex align-items-start justify-content-between mb-2">
                  <code class="text-warning font-weight-bold small">{{ method.name }}</code>
                  <b-button
                    size="sm"
                    variant="link"
                    @click="copyToClipboard(method.usage)"
                    class="p-0 text-secondary"
                    v-b-tooltip.hover
                    title="Copy to clipboard"
                  >
                    <i class="material-icons icon-16pt">content_copy</i>
                  </b-button>
                </div>
                <p class="text-muted small mb-2">{{ method.description }}</p>
                <code class="d-block p-2 bg-light rounded small text-wrap">{{ method.usage }}</code>
              </b-card>
            </div>
          </div>
          <div v-if="filteredMethods.length === 0" class="text-center py-5 text-muted">
            <i class="material-icons" style="font-size: 48px">search_off</i>
            <p class="mb-0">No results found</p>
          </div>
        </b-tab>

        <b-tab>
          <template #title>
            <i class="material-icons icon-16pt mr-1">bolt</i>
            <span class="small">Events</span>
          </template>
          <div v-for="category in filteredEvents" :key="category.name" class="mb-3">
            <b-badge variant="danger" class="mb-2 text-uppercase" style="font-size: 10px">{{
              category.name
            }}</b-badge>
            <div v-for="event in category.items" :key="event.name" class="mb-2">
              <b-card class="border-left-danger">
                <div class="d-flex align-items-start justify-content-between mb-2">
                  <code class="text-danger font-weight-bold small">{{ event.name }}</code>
                  <b-button
                    size="sm"
                    variant="link"
                    @click="copyToClipboard(event.usage)"
                    class="p-0 text-secondary"
                    v-b-tooltip.hover
                    title="Copy to clipboard"
                  >
                    <i class="material-icons icon-16pt">content_copy</i>
                  </b-button>
                </div>
                <p class="text-muted small mb-2">{{ event.description }}</p>
                <code class="d-block p-2 bg-light rounded small text-wrap">{{ event.usage }}</code>
              </b-card>
            </div>
          </div>
          <div v-if="filteredEvents.length === 0" class="text-center py-5 text-muted">
            <i class="material-icons" style="font-size: 48px">search_off</i>
            <p class="mb-0">No results found</p>
          </div>
        </b-tab>

        <b-tab>
          <template #title>
            <i class="material-icons icon-16pt mr-1">storage</i>
            <span class="small">Data</span>
          </template>
          <div v-for="prop in filteredDataProperties" :key="prop.name" class="mb-2">
            <b-card class="border-left-info">
              <div class="d-flex align-items-start justify-content-between mb-2">
                <code class="text-info font-weight-bold small">{{ prop.name }}</code>
                <b-button
                  size="sm"
                  variant="link"
                  @click="copyToClipboard(prop.usage)"
                  class="p-0 text-secondary"
                  v-b-tooltip.hover
                  title="Copy to clipboard"
                >
                  <i class="material-icons icon-16pt">content_copy</i>
                </b-button>
              </div>
              <p class="text-muted small mb-2">{{ prop.description }}</p>
              <code class="d-block p-2 bg-light rounded small text-wrap">{{ prop.usage }}</code>
            </b-card>
          </div>
          <div v-if="filteredDataProperties.length === 0" class="text-center py-5 text-muted">
            <i class="material-icons" style="font-size: 48px">search_off</i>
            <p class="mb-0">No results found</p>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </fmv-sidebar>
</template>

<script>
import { FmvSidebar } from 'fmv-layout';

export default {
  name: 'ReferenceSidebar',

  components: {
    FmvSidebar,
  },

  data() {
    return {
      searchQuery: '',
      computedProperties: [
        {
          name: 'Banner Section',
          items: [
            { name: 'bannerTitle', description: 'Main heading text in hero banner', usage: '{{ bannerTitle }}' },
            { name: 'bannerDesc', description: 'Description text (supports HTML)', usage: 'v-html="bannerDesc"' },
            { name: 'bannerImage', description: 'Banner background/hero image URL', usage: ':src="bannerImage"' },
            { name: 'bannerCtaTitle', description: 'Text on banner CTA button', usage: '{{ bannerCtaTitle }}' },
            { name: 'bannerCtaLink', description: 'URL for banner CTA button', usage: ':href="bannerCtaLink"' },
            {
              name: 'bannerCtaColor',
              description: 'Text color for CTA button',
              usage: ':style="{ color: bannerCtaColor }"',
            },
            {
              name: 'bannerTitleStyle',
              description: 'Responsive font size for title',
              usage: ':style="bannerTitleStyle"',
            },
            {
              name: 'bannerDescStyle',
              description: 'Responsive font size for description',
              usage: ':style="bannerDescStyle"',
            },
            {
              name: 'showBannerCtaWithLink',
              description: 'Show CTA with direct link',
              usage: 'v-if="showBannerCtaWithLink"',
            },
            {
              name: 'showBannerCtaWithModal',
              description: 'Show CTA with location modal',
              usage: 'v-if="showBannerCtaWithModal"',
            },
          ],
        },
        {
          name: 'Programs Section',
          items: [
            { name: 'programsTitle', description: 'Section heading for programs', usage: '{{ programsTitle }}' },
            {
              name: 'programTextAlign',
              description: 'Text alignment for program cards',
              usage: ':style="\'text-align: \' + programTextAlign"',
            },
            {
              name: 'getProgramsData',
              description: 'Array of program objects',
              usage: 'v-for="item in getProgramsData"',
            },
          ],
        },
        {
          name: 'Stats Section',
          items: [
            {
              name: 'showStatsSection',
              description: 'Controls stats section visibility',
              usage: 'v-if="showStatsSection"',
            },
            { name: 'statsTitle', description: 'Section heading for statistics', usage: '{{ statsTitle }}' },
            { name: 'statsDesc', description: 'Description for stats section', usage: '{{ statsDesc }}' },
            { name: 'statsItems', description: 'Array of stat objects', usage: 'v-for="attrib in statsItems"' },
          ],
        },
        {
          name: 'Features Section',
          items: [
            {
              name: 'showFeaturesSection',
              description: 'Controls features section visibility',
              usage: 'v-if="showFeaturesSection"',
            },
            { name: 'featuresTitle', description: 'Section heading for features', usage: '{{ featuresTitle }}' },
            {
              name: 'featuresItems',
              description: 'Array of feature objects',
              usage: 'v-for="feature in featuresItems"',
            },
          ],
        },
        {
          name: 'Classes Section',
          items: [
            { name: 'classesTitle', description: 'Section heading for classes', usage: '{{ classesTitle }}' },
            { name: 'classesDesc', description: 'Description for classes section', usage: '{{ classesDesc }}' },
            { name: 'classVideoUrl', description: 'Video URL for classes section', usage: ':src="classVideoUrl"' },
            { name: 'defaultImageUrl', description: 'Fallback image when no video', usage: ':src="defaultImageUrl"' },
            { name: 'getClassesData', description: 'Array of class objects', usage: 'v-for="item in getClassesData"' },
          ],
        },
        {
          name: 'Testimonials Section',
          items: [
            {
              name: 'showTestimonialsSection',
              description: 'Controls testimonials visibility',
              usage: 'v-if="showTestimonialsSection"',
            },
            {
              name: 'testimonialsTitle',
              description: 'Section heading for testimonials',
              usage: '{{ testimonialsTitle }}',
            },
            {
              name: 'googleReviews',
              description: 'Array of Google review objects',
              usage: 'v-for="item in googleReviews"',
            },
            { name: 'googleReviewUrl', description: 'Link to Google reviews page', usage: ':href="googleReviewUrl"' },
          ],
        },
        {
          name: 'Bottom CTA Section',
          items: [
            {
              name: 'showBottomSection',
              description: 'Controls bottom CTA visibility',
              usage: 'v-if="showBottomSection"',
            },
            { name: 'bottomTitle', description: 'Heading for bottom CTA', usage: '{{ bottomTitle }}' },
            { name: 'bottomDesc', description: 'Description for bottom CTA', usage: '{{ bottomDesc }}' },
            { name: 'bottomCtaTitle', description: 'Text for bottom CTA button', usage: '{{ bottomCtaTitle }}' },
            { name: 'bottomCtaLink', description: 'URL for bottom CTA button', usage: ':href="bottomCtaLink"' },
            {
              name: 'showBottomCtaWithLink',
              description: 'Show bottom CTA with link',
              usage: 'v-if="showBottomCtaWithLink"',
            },
            {
              name: 'showBottomCtaWithModal',
              description: 'Show bottom CTA with modal',
              usage: 'v-if="showBottomCtaWithModal"',
            },
          ],
        },
        {
          name: 'Utility Properties',
          items: [
            { name: 'schoolLandingUrl', description: 'URL to school landing page', usage: ':href="schoolLandingUrl"' },
            { name: 'hasTopBar', description: 'Checks if top bar is enabled', usage: 'v-if="hasTopBar"' },
            { name: 'isDesktop', description: 'True when viewport > 1200px', usage: 'v-if="isDesktop"' },
            { name: 'isTablet', description: 'True for tablet viewports', usage: 'v-if="isTablet"' },
            { name: 'isMobile', description: 'True for mobile viewports', usage: 'v-if="isMobile"' },
            { name: 'isMobSmallScreen', description: 'True when viewport <= 776px', usage: 'v-if="isMobSmallScreen"' },
          ],
        },
      ],
      methods: [
        {
          name: 'URL Generation',
          items: [
            {
              name: 'getProgramUrl(item)',
              description: 'Generates URL to program detail page',
              usage: ':href="getProgramUrl(item)"',
            },
            {
              name: 'getClassUrl(classItem)',
              description: 'Generates URL to class detail page',
              usage: ':href="getClassUrl(item)"',
            },
          ],
        },
        {
          name: 'Fee Calculation',
          items: [
            {
              name: 'getTuitionFee(item)',
              description: 'Calculates total tuition fee',
              usage: '{{ getTuitionFee(item) }}',
            },
            {
              name: 'getInitialFee(item)',
              description: 'Calculates initial fee before discount',
              usage: '{{ getInitialFee(item) }}',
            },
            { name: 'getSavings(item)', description: 'Calculates discount savings', usage: '{{ getSavings(item) }}' },
          ],
        },
        {
          name: 'Date Formatting',
          items: [
            {
              name: 'month(date)',
              description: 'Formats to 3-letter month (Jan, Feb)',
              usage: '{{ month(item.start_date) }}',
            },
            { name: 'day(date)', description: 'Formats to 2-digit day (01, 15)', usage: '{{ day(item.start_date) }}' },
            { name: 'year(date)', description: 'Formats to 4-digit year (2025)', usage: '{{ year(item.start_date) }}' },
          ],
        },
      ],
      events: [
        {
          name: 'Modal Events',
          items: [
            {
              name: 'openLocationModal()',
              description: 'Opens location selection modal',
              usage: '@click="openLocationModal"',
            },
            { name: 'hideLocationModal()', description: 'Closes location modal', usage: '@close="hideLocationModal"' },
          ],
        },
        {
          name: 'Video Events',
          items: [
            { name: 'handleVideoOpen()', description: 'Opens video player modal', usage: '@click="handleVideoOpen()"' },
            {
              name: 'handleVideoClose()',
              description: 'Closes video player modal',
              usage: '@click="handleVideoClose()"',
            },
          ],
        },
        {
          name: 'Carousel Events',
          items: [
            { name: 'slickNext()', description: 'Navigate to next slide', usage: '@click="slickNext()"' },
            { name: 'slickPrev()', description: 'Navigate to previous slide', usage: '@click="slickPrev()"' },
            {
              name: 'handleGoTo(slide)',
              description: 'Navigate to specific slide',
              usage: '@click="handleGoTo(index)"',
            },
            {
              name: 'handleAfterChange()',
              description: 'Updates active slide indicator',
              usage: '@afterChange="handleAfterChange"',
            },
          ],
        },
      ],
      dataProperties: [
        { name: 'showModal', description: 'Controls location modal visibility', usage: 'v-model="showModal"' },
        { name: 'dialogVideo', description: 'Controls video player modal visibility', usage: 'v-model="dialogVideo"' },
        { name: 'slickOptions', description: 'Class video carousel configuration', usage: ':options="slickOptions"' },
        {
          name: 'testSlickOptions',
          description: 'Testimonials carousel configuration',
          usage: ':options="testSlickOptions"',
        },
        {
          name: 'expertiseSlickOptions',
          description: 'Expertise carousel configuration',
          usage: ':options="expertiseSlickOptions"',
        },
        { name: 'defaultRatings', description: 'Default rating descriptions', usage: 'defaultRatings[rating]' },
      ],
    };
  },

  computed: {
    filteredComputedProperties() {
      if (!this.searchQuery) return this.computedProperties;
      const query = this.searchQuery.toLowerCase();
      return this.computedProperties
        .map((category) => ({
          ...category,
          items: category.items.filter(
            (item) => item.name.toLowerCase().includes(query) || item.description.toLowerCase().includes(query)
          ),
        }))
        .filter((category) => category.items.length > 0);
    },

    filteredMethods() {
      if (!this.searchQuery) return this.methods;
      const query = this.searchQuery.toLowerCase();
      return this.methods
        .map((category) => ({
          ...category,
          items: category.items.filter(
            (item) => item.name.toLowerCase().includes(query) || item.description.toLowerCase().includes(query)
          ),
        }))
        .filter((category) => category.items.length > 0);
    },

    filteredEvents() {
      if (!this.searchQuery) return this.events;
      const query = this.searchQuery.toLowerCase();
      return this.events
        .map((category) => ({
          ...category,
          items: category.items.filter(
            (item) => item.name.toLowerCase().includes(query) || item.description.toLowerCase().includes(query)
          ),
        }))
        .filter((category) => category.items.length > 0);
    },

    filteredDataProperties() {
      if (!this.searchQuery) return this.dataProperties;
      const query = this.searchQuery.toLowerCase();
      return this.dataProperties.filter(
        (item) => item.name.toLowerCase().includes(query) || item.description.toLowerCase().includes(query)
      );
    },
  },

  methods: {
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$emit('copy-success', text);
      });
    },
  },
};
</script>

<style>
/* fmv-sidebar handles scrolling automatically with wheel-propagation */

/* Make tabs navigation horizontally scrollable */
.nav-pills {
  flex-wrap: nowrap !important;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  display: flex !important;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin !important;
  scrollbar-color: #e0e0e0 transparent !important;
}

.nav-pills::-webkit-scrollbar {
  height: 3px !important;
}

.nav-pills::-webkit-scrollbar-track {
  background: transparent !important;
  border-radius: 2px !important;
}

.nav-pills::-webkit-scrollbar-thumb {
  background: #e0e0e0 !important;
  border-radius: 2px !important;
}

.nav-pills::-webkit-scrollbar-thumb:hover {
  background: #d0d0d0 !important;
}

.nav-pills .nav-item {
  flex-shrink: 0 !important;
  white-space: nowrap;
}

.nav-pills .nav-link {
  white-space: nowrap !important;
  padding: 0.25rem 0.75rem !important;
  font-size: 0.75rem !important;
  border-radius: 50px !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.25rem;
}

.nav-pills .nav-link .material-icons {
  font-size: 14px !important;
}

.nav-pills .nav-link .small {
  font-size: 0.75rem !important;
}
</style>
